import React, { useEffect, useRef, useState } from "react";
import { IconButton, Input, Option, Select, Textarea, Tooltip, Button, Spinner } from "@material-tailwind/react";
import { convertToPresentableText, Notify, NotifyError } from "../../../utils/functions";
import { DEAL_STAGES, DEAL_STAGES_ENUM, LEVELS_ENUM, MARKETING_CHANNELS_ENUM, RELATIONSHIP_STRENGTHS_ENUM } from "../../../utils/enums";
import { useCountries } from "use-react-countries";
import { ChevronDownIcon, InfoIcon, PaperclipIcon, TrashIcon, DownloadIcon, FileIcon } from "lucide-react";
import { phoneNumberInputInstructionsTemplate } from "../../../utils/templates";

const BasicInfoTab = ({ formData, handlers, salesReps }) => {
  const {
    handleInputChange,
    handleSelectChange,
    handleAddAttachment,
    handleRemoveAttachment
  } = handlers;

  const { countries } = useCountries();
  
  // Derive selected country from formData, fallback to Pakistan (+92)
  let countryIndex = countries.findIndex(c => c.countryCallingCode === (formData?.countryCode || "+92"));
  if (countryIndex === -1) countryIndex = countries.findIndex(c => c.countryCallingCode === "+92");
  const { name, flags, countryCallingCode } = countries[countryIndex];

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchCountry, setSearchCountry] = useState("");
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Limit file size to 5MB
    if (file.size > 5 * 1024 * 1024) {
      NotifyError("File is too large. Maximum size allowed is 5MB.");
      e.target.value = null;
      return;
    }

    // Restrict file types
    const allowedTypes = [
      'application/pdf', // .pdf
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'image/jpeg', // .jpg, .jpeg
      'image/png', // .png
      'text/csv' // .csv
    ];
    
    if (!allowedTypes.includes(file.type)) {
      NotifyError("Invalid file type. Only PDF, Word, JPEG, PNG, and CSV are allowed.");
      e.target.value = null;
      return;
    }

    setIsUploadingFile(true);
    
    try {
      // Convert file to Base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = () => {
        const base64String = reader.result;
        
        handleAddAttachment({
          name: file.name,
          url: base64String, // Store the Base64 string directly in MongoDB
          type: file.type,
          size: file.size,
          uploadedAt: new Date().toISOString()
        });
        
        Notify("File attached successfully", "success");
        setIsUploadingFile(false);
        e.target.value = null; // Reset input
      };

      reader.onerror = (error) => {
        console.error("Error reading file:", error);
        NotifyError("Failed to read file", error);
        setIsUploadingFile(false);
        e.target.value = null;
      };

    } catch (error) {
      console.error("Error processing file:", error);
      NotifyError("Failed to process file", error);
      setIsUploadingFile(false);
      e.target.value = null;
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setSearchCountry("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (dropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [dropdownOpen]);

  // Handle user typing country code in phone number field
  useEffect(() => {
    if (
      formData.phoneNumber &&
      formData.phoneNumber.length > 1 &&
      formData.phoneNumber.length <= 3 &&
      (
        formData.phoneNumber.startsWith("+") ||
        formData.phoneNumber.startsWith("00") ||
        formData.phoneNumber.startsWith("0") ||
        formData.phoneNumber.startsWith("92")
      )
    ) {
      const prefix = formData.phoneNumber.startsWith("92") ? "+" : "";
      const typedCode = prefix + formData.phoneNumber;
      const cIndex = countries.findIndex(c => c.countryCallingCode === typedCode);
      if (cIndex !== -1) {
        handleInputChange({ target: { name: "phoneNumber", value: "" } });
        handleInputChange({ target: { name: "countryCode", value: typedCode } });
      }
    }
  }, [formData.phoneNumber, handleInputChange, countries]);

  return (
    <div className="space-y-4">

      <div className="flex gap-4 flex-col md:flex-row">
        <Select
          label="Lead Status"
          labelProps={{
            className: `after:content-['*'] after:text-red-500 after:ml-1 after:mb-[3px]`
          }}
          value={formData.leadStatus}
          onChange={(value) => handleSelectChange("leadStatus", value)}
        >
          {DEAL_STAGES_ENUM.map((status, index) => (
            <Option key={index} value={status}>
              {convertToPresentableText(status)}
            </Option>
          ))}
        </Select>

        <Select
          label="Lead Source"
          labelProps={{
            className: `after:content-['*'] after:text-red-500 after:ml-1 after:mb-[3px]`
          }}
          value={formData.leadSource}
          onChange={(value) => handleSelectChange("leadSource", value)}
          required
        >
          {MARKETING_CHANNELS_ENUM.map((source, index) => (
            <Option key={index} value={source}>
              {convertToPresentableText(source)}
            </Option>
          ))}
        </Select>

      </div>
      <div className="flex gap-4 flex-col md:flex-row">
        <Select
          label="Engagement Level"
          value={formData.engagementLevel}
          onChange={(value) => handleSelectChange("engagementLevel", value)}
        >
          {LEVELS_ENUM.map((level, index) => (
            <Option key={index} value={level}>
              {convertToPresentableText(level)}
            </Option>
          ))}
        </Select>
        <Select
          label="Purchase Intent"
          value={formData.purchaseIntent}
          onChange={(value) => handleSelectChange("purchaseIntent", value)}
        >
          {RELATIONSHIP_STRENGTHS_ENUM.map((intent, index) => (
            <Option key={index} value={intent}>
              {convertToPresentableText(intent)}
            </Option>
          ))}
        </Select>
      </div>
      <div className="flex gap-4 flex-col md:flex-row">
        <Input
          label="Required Seats"
          name="desiredSeatsCount"
          type="number"
          value={formData.desiredSeatsCount}
          onChange={handleInputChange}
        />
        <Input
          label="Required Offices"
          name="desiredOfficesCount"
          type="number"
          value={formData.desiredOfficesCount}
          onChange={handleInputChange}
        />

        <Input
          label="Connection Date"
          name="connectionDate"
          type="datetime-local"
          value={formData.connectionDate}
          onChange={handleInputChange}
        />
      </div>
      <hr />
      <div className="flex gap-4 flex-col md:flex-row">
        <Input
          label="Full Name"
          name="fullName"
          value={formData.fullName}
          onChange={handleInputChange}
          required
        />
        {/* <Input
          label="Phone Number"
          required={formData.leadStatus === DEAL_STAGES.WON}
          name="phoneNumber"
          placeholder="+92XXXXXXXXXX"
          value={formData.phoneNumber}
          onChange={handleInputChange}
        /> */}
        <div className="relative flex w-full max-w-[24rem]">
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex h-10 items-center gap-2 rounded-l-md border border-r-0 border-blue-gray-200 bg-blue-gray-500/10 pl-3 pr-2 text-sm text-blue-gray-700 hover:bg-blue-gray-500/20 transition-colors"
            >
              <img
                src={flags.svg}
                alt={name}
                className="h-4 w-4 rounded-full object-cover"
              />
              {countryCallingCode}
              <ChevronDownIcon size={14} className={`transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {dropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-[18rem] max-h-[20rem] bg-white border border-blue-gray-100 rounded-lg shadow-lg z-[10000] flex flex-col">
                <div className="p-2 border-b border-blue-gray-100">
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search country..."
                    value={searchCountry}
                    onChange={(e) => setSearchCountry(e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-blue-gray-200 rounded-md outline-none focus:border-gray-900 bg-transparent placeholder:text-blue-gray-300"
                  />
                </div>
                <div className="overflow-y-auto flex-1">
                  {countries
                    .map((c, idx) => ({ ...c, originalIndex: idx }))
                    .filter(({ name, countryCallingCode }) =>
                      name.toLowerCase().includes(searchCountry.toLowerCase()) ||
                      countryCallingCode.includes(searchCountry)
                    )
                    .map(({ name, flags, countryCallingCode, originalIndex }) => (
                      <div
                        key={name}
                        className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-blue-gray-50 transition-colors"
                        onClick={() => {
                          handleInputChange({ target: { name: "countryCode", value: countryCallingCode } });
                          setDropdownOpen(false);
                          setSearchCountry("");
                        }}
                      >
                        <img
                          src={flags.svg}
                          alt={name}
                          className="h-5 w-5 rounded-full object-cover"
                        />
                        {name} <span className="ml-auto text-blue-gray-400">{countryCallingCode}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
          <Input
            type="tel"
            placeholder="Mobile Number"
            className="rounded-l-none !border-t-blue-gray-200 focus:!border-t-gray-900 placeholder:opacity-100"
            required={true}
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleInputChange}
            labelProps={{ className: "hidden" }}
            containerProps={{ className: "min-w-0" }}
            icon={<Tooltip content={phoneNumberInputInstructionsTemplate} placement="top" className="z-[10000]">
              <IconButton className="rounded-full h-4 w-4" size="sm">
                <InfoIcon size={14} />
              </IconButton>
            </Tooltip>}
          />
        </div>

      </div>

      <div className="flex gap-4 flex-col md:flex-row">
        <Input
          label="Email Address"
          name="emailAddress"
          type="email"
          value={formData.emailAddress}
          required={formData.leadStatus === DEAL_STAGES.WON}
          onChange={handleInputChange}
        />
        <Input
          label="Secondary Email"
          name="secondaryEmail"
          type="email"
          value={formData.secondaryEmail}
          onChange={handleInputChange}
        />
        <Input
          label="CNIC"
          name="cnic"
          value={formData.cnic}
          required={formData.leadStatus === DEAL_STAGES.WON}
          onChange={handleInputChange}
        />
      </div>

      <div className="flex gap-4 flex-col md:flex-row">
        <Input
          label="Contact Person Job Title"
          name="jobTitle"
          value={formData.jobTitle}
          onChange={handleInputChange}
        />
        <Input
          label="Additional Mobile Number"
          name="mobileNumber"
          value={formData.mobileNumber}
          onChange={handleInputChange}
        />
      </div>



      <div className="w-full">
        <Textarea
          label="Additional Notes (Optional)"
          name="notes"
          value={formData.notes || ''}
          onChange={handleInputChange}
        />
      </div>
      <hr />
      <div className="flex gap-4 flex-col md:flex-row">
        <Select
          label="Assigned Sales Rep"
          value={formData.assignedSalesRep}
          onChange={(value) => handleSelectChange("assignedSalesRep", value)}
        >
          {salesReps?.map((rep) => (
            <Option key={rep._id} value={rep._id}>
              {rep.name}
            </Option>
          ))}
        </Select>
        <Input
          label="Assignment Date"
          name="assignmentDate"
          type="date"
          value={formData.assignmentDate ? new Date(formData.assignmentDate).toISOString().split('T')[0] : ''}
          onChange={handleInputChange}
        />

      </div>

      <hr />
      <div className="flex flex-col gap-2">
        <div className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
          <PaperclipIcon size={16} /> Attachments (CVs, Documents, etc.)
        </div>
        <div className="flex flex-col gap-3">
          {formData.attachments && formData.attachments.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {formData.attachments.map((file, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 border border-gray-200 rounded-lg bg-gray-50/50 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-2 overflow-hidden">
                    <div className="p-1.5 bg-blue-50 text-blue-500 rounded-md shrink-0">
                      <FileIcon size={14} />
                    </div>
                    <span className="text-xs text-gray-700 truncate font-medium" title={file.name}>{file.name}</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Tooltip content="Download File">
                      <a href={file.url} download={file.name}>
                        <IconButton size="sm" variant="text" color="blue" className="rounded-full h-7 w-7">
                          <DownloadIcon size={14} />
                        </IconButton>
                      </a>
                    </Tooltip>
                    <Tooltip content="Remove File">
                      <IconButton 
                        size="sm" 
                        variant="text" 
                        color="red" 
                        className="rounded-full h-7 w-7"
                        onClick={() => handleRemoveAttachment(idx)}
                      >
                        <TrashIcon size={14} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-4 mt-1">
            <input 
              type="file" 
              id="lead-attachment-upload" 
              className="hidden" 
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.csv"
              onChange={handleFileUpload}
              disabled={isUploadingFile}
            />
            <Button 
              size="sm" 
              variant="outlined" 
              color="blue-gray"
              className="flex items-center gap-2 rounded-full border-blue-gray-200 text-blue-gray-600 hover:bg-blue-gray-50"
              onClick={() => document.getElementById('lead-attachment-upload').click()}
              disabled={isUploadingFile}
            >
              {isUploadingFile ? <Spinner className="h-4 w-4" /> : <PaperclipIcon size={14} />}
              {isUploadingFile ? "Uploading..." : "Upload File"}
            </Button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default BasicInfoTab;